import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  JOURNAL_ASSETS,
  JOURNAL_PAGES,
  JOURNAL_PAGE_THRESHOLDS,
} from "./restorationJournalData";
import "./UvJournal.css";

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

/** Spotlight lands on the page directly above the torch lens. */
function getBeamHitOnBook(originX, originY, bookLeft, bookTop, bookRight, bookBottom) {
  const pad = 10;
  const innerLeft = bookLeft + pad;
  const innerTop = bookTop + pad;
  const innerRight = bookRight - pad;
  const innerBottom = bookBottom - pad;

  const hitX = clamp(originX, innerLeft, innerRight);

  if (originY >= innerBottom) {
    return { hitX, hitY: innerBottom - 14 };
  }
  if (originY <= innerTop) {
    return { hitX, hitY: innerTop + 14 };
  }

  const upwardReach = Math.max(48, (originY - innerTop) * 0.55);
  return {
    hitX,
    hitY: clamp(originY - upwardReach, innerTop, innerBottom),
  };
}

function pageIndexFromScan(scan) {
  let idx = 0;
  for (let i = 0; i < JOURNAL_PAGE_THRESHOLDS.length; i += 1) {
    if (scan >= JOURNAL_PAGE_THRESHOLDS[i]) idx = i;
  }
  return idx;
}

/**
 * Journal UV scan: torch starts on the right; dragging over the book
 * advances book1 → book5 and shows a UV beam from the torch.
 */
export default function UvJournal({
  onComplete,
  onScanChange,
  disabled = false,
}) {
  const boardRef = useRef(null);
  const bookRef = useRef(null);
  const draggingRef = useRef(false);
  const completedRef = useRef(false);
  const scanRef = useRef(0);
  const lastPointRef = useRef(null);
  const overBookRef = useRef(false);
  const holdTimerRef = useRef(0);
  const torchReadyRef = useRef(false);
  const [dragging, setDragging] = useState(false);

  const [torch, setTorch] = useState({ x: 0, y: 0, ready: false });
  const [beam, setBeam] = useState(null);
  const [scan, setScan] = useState(0);
  const [holdingFinal, setHoldingFinal] = useState(false);
  const [torchSize, setTorchSize] = useState({ w: 70, h: 150 });

  const pageIndex = pageIndexFromScan(scan);
  const revealPageIndex = Math.min(pageIndex + 1, JOURNAL_PAGES.length - 1);
  const onFinalPage = pageIndex >= JOURNAL_PAGES.length - 1;
  const done = holdingFinal || (onFinalPage && scan >= 0.98);

  useEffect(() => {
    return () => {
      if (holdTimerRef.current) window.clearTimeout(holdTimerRef.current);
      document.body.classList.remove("uv-torch-dragging");
    };
  }, []);

  /**
   * Place / size the torch.
   * - resetPosition: snap to the rest gutter (initial + resize only)
   * - Never yank the torch back while dragging, or when the book page
   *   image reloads mid-scan (that was causing a ghost torch on the right).
   */
  const placeTorchRest = useCallback(({ resetPosition = false } = {}) => {
    const board = boardRef.current;
    const book = bookRef.current;
    if (!board) return;
    const br = board.getBoundingClientRect();
    const narrow =
      br.width < 560 ||
      window.matchMedia("(max-width: 900px)").matches;

    const w = narrow
      ? clamp(br.width * 0.13, 44, 62)
      : clamp(br.width * 0.09, 56, 86);
    const h = w * 2.15;
    setTorchSize({ w, h });

    if (draggingRef.current) return;
    if (!resetPosition && torchReadyRef.current) return;

    let x;
    let y;
    if (narrow && book) {
      const bookRect = book.getBoundingClientRect();
      const bookTop = bookRect.top - br.top;
      x = clamp(br.width - w - 2, 0, br.width - w);
      y = clamp(
        bookTop + bookRect.height * 0.28,
        4,
        Math.max(4, br.height - h - 4)
      );
    } else {
      x = br.width - w - 12;
      y = br.height * 0.28;
    }

    torchReadyRef.current = true;
    setTorch({ x, y, ready: true });
  }, []);

  useEffect(() => {
    placeTorchRest({ resetPosition: true });

    const onResize = () => {
      if (draggingRef.current) return;
      placeTorchRest({ resetPosition: true });
    };

    const onBookImgLoad = () => {
      // Page swaps during scan fire load — only size/initial place, never snap back
      placeTorchRest({ resetPosition: false });
    };

    window.addEventListener("resize", onResize);
    const bookImg = bookRef.current?.querySelector("img");
    bookImg?.addEventListener("load", onBookImgLoad);
    return () => {
      window.removeEventListener("resize", onResize);
      bookImg?.removeEventListener("load", onBookImgLoad);
    };
  }, [placeTorchRest]);

  const beamFromTorch = useCallback((torchX, torchY, size) => {
    const book = bookRef.current;
    const board = boardRef.current;
    if (!book || !board) return null;
    const bookRect = book.getBoundingClientRect();
    const boardRect = board.getBoundingClientRect();

    // Torch head (light origin) ≈ top-center of the torch image
    const originX = torchX + size.w * 0.5;
    const originY = torchY + size.h * 0.08;

    const bookLeft = bookRect.left - boardRect.left;
    const bookTop = bookRect.top - boardRect.top;
    const bookRight = bookLeft + bookRect.width;
    const bookBottom = bookTop + bookRect.height;

    const inside =
      originX >= bookLeft - 40 &&
      originX <= bookRight + 40 &&
      originY >= bookTop - 40 &&
      originY <= bookBottom + size.h * 0.65;

    if (!inside) return null;

    const { hitX, hitY } = getBeamHitOnBook(
      originX,
      originY,
      bookLeft,
      bookTop,
      bookRight,
      bookBottom
    );

    const radiusX = bookRect.width * 0.22;
    const radiusY = radiusX * 1.22;
    const beamLength = Math.max(originY - hitY, 36);
    // Full cone — do not clamp to the board (that caused the hard crop)
    const beamWidth = Math.max(radiusX * 2.6, size.w * 1.6);
    const beamLeft = originX - beamWidth * 0.5;

    return {
      originX,
      originY,
      hitX,
      hitY,
      radiusX,
      radiusY,
      beamLength,
      beamWidth,
      beamLeft,
      bookHitX: hitX - bookLeft,
      bookHitY: hitY - bookTop,
      bookW: bookRect.width,
      bookH: bookRect.height,
    };
  }, []);

  const endDrag = useCallback(() => {
    draggingRef.current = false;
    setDragging(false);
    document.body.classList.remove("uv-torch-dragging");
    if (!overBookRef.current) setBeam(null);
  }, []);

  const applyTorchAt = useCallback(
    (clientX, clientY) => {
      const board = boardRef.current;
      if (!board) return;
      const rect = board.getBoundingClientRect();
      const x = clamp(
        clientX - rect.left - torchSize.w * 0.5,
        -torchSize.w * 0.2,
        rect.width - torchSize.w * 0.55
      );
      const y = clamp(
        clientY - rect.top - torchSize.h * 0.12,
        -torchSize.h * 0.05,
        rect.height - torchSize.h * 0.4
      );

      setTorch({ x, y, ready: true });
      torchReadyRef.current = true;

      const nextBeam = beamFromTorch(x, y, torchSize);
      const isOver = Boolean(nextBeam);
      overBookRef.current = isOver;
      setBeam(nextBeam);

      if (!isOver || disabled || completedRef.current) {
        lastPointRef.current = { x: clientX, y: clientY };
        return;
      }

      const last = lastPointRef.current;
      lastPointRef.current = { x: clientX, y: clientY };
      if (!last) return;

      const dist = Math.hypot(clientX - last.x, clientY - last.y);
      // Sweeping builds reveal; parking barely advances
      const gain = Math.min(0.045, dist / 900 + 0.0012);
      const nextScan = clamp(scanRef.current + gain, 0, 1);
      if (nextScan - scanRef.current < 0.001 && nextScan < 1) return;

      scanRef.current = nextScan;
      setScan(nextScan);
      onScanChange?.(nextScan);

      if (nextScan >= 0.98 && !completedRef.current) {
        completedRef.current = true;
        setHoldingFinal(true);
        endDrag();
        if (holdTimerRef.current) window.clearTimeout(holdTimerRef.current);
        holdTimerRef.current = window.setTimeout(() => {
          onComplete?.();
        }, 3500);
      }
    },
    [beamFromTorch, disabled, endDrag, onComplete, onScanChange, torchSize]
  );

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current || disabled) return;
      applyTorchAt(e.clientX, e.clientY);
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      endDrag();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [applyTorchAt, disabled, endDrag]);

  const onTorchPointerDown = useCallback(
    (e) => {
      if (disabled || done) return;
      if (e.button != null && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      draggingRef.current = true;
      setDragging(true);
      document.body.classList.add("uv-torch-dragging");
      e.currentTarget.setPointerCapture?.(e.pointerId);
      lastPointRef.current = { x: e.clientX, y: e.clientY };
      applyTorchAt(e.clientX, e.clientY);
    },
    [applyTorchAt, disabled, done]
  );

  return (
    <div
      ref={boardRef}
      className={`uv-journal${done ? " uv-journal--done" : ""}`}
      role="application"
      aria-label="Drag the UV torch from the right onto the journal to reveal hidden notes."
    >
      <div
        ref={bookRef}
        className="uv-journal__book-wrap"
        style={
          beam
            ? {
                "--uv-x": `${(beam.bookHitX / beam.bookW) * 100}%`,
                "--uv-y": `${(beam.bookHitY / beam.bookH) * 100}%`,
                "--uv-rx": `${beam.radiusX}px`,
                "--uv-ry": `${beam.radiusY}px`,
              }
            : undefined
        }
      >
        <img
          src={JOURNAL_PAGES[pageIndex]}
          alt=""
          className="uv-journal__book"
          draggable={false}
        />

        {beam && revealPageIndex > pageIndex ? (
          <div className="uv-journal__reveal" aria-hidden>
            <img
              src={JOURNAL_PAGES[revealPageIndex]}
              alt=""
              className="uv-journal__book uv-journal__book--reveal"
              draggable={false}
            />
          </div>
        ) : null}
      </div>

      {beam ? (
        <>
          <div
            className="uv-journal__beam"
            style={{
              left: beam.beamLeft,
              top: beam.hitY,
              width: beam.beamWidth,
              height: beam.beamLength,
              "--beam-apex": `${
                ((beam.originX - beam.beamLeft) / beam.beamWidth) * 100
              }%`,
            }}
            aria-hidden
          />
          <div
            className="uv-journal__uv-tint"
            style={{
              left: beam.hitX,
              top: beam.hitY,
              width: beam.radiusX * 2,
              height: beam.radiusY * 2,
            }}
            aria-hidden
          />
          <div
            className="uv-journal__uv-glow"
            style={{
              left: beam.hitX,
              top: beam.hitY,
              width: beam.radiusX * 2,
              height: beam.radiusY * 2,
            }}
            aria-hidden
          />
          <div
            className="uv-journal__torch-glow"
            style={{
              left: beam.originX,
              top: beam.originY,
            }}
            aria-hidden
          />
        </>
      ) : null}

      {torch.ready ? (
        <button
          type="button"
          className={`uv-journal__torch${dragging ? " uv-journal__torch--lifted" : ""}`}
          style={{
            width: torchSize.w,
            height: torchSize.h,
            transform: `translate3d(${torch.x}px, ${torch.y}px, 0)`,
            backgroundImage: `url(${JOURNAL_ASSETS.torch})`,
          }}
          aria-label="UV torch"
          onPointerDown={onTorchPointerDown}
        />
      ) : null}

      <p className="uv-journal__progress" aria-live="polite">
        {holdingFinal
          ? "Hidden notes revealed — read carefully…"
          : done
            ? "Hidden notes fully revealed"
            : `Revealing… ${Math.round(scan * 100)}%`}
      </p>
    </div>
  );
}
